import React, { CSSProperties, useState } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { mergeQueryParameters } from '@/utils/UriBuilder'
import { formatYouTubeUrl } from '@/utils/url'

interface EmbeddedGiveawayVideoProps {
  id?: string
  className?: string
  containerClassName?: string
  title?: string
  url: string
  thumbnail?: string
  onPlayClick?: () => void
  iframeStyle?: CSSProperties
  autoPlayInView?: boolean
}

export const EmbeddedGiveawayVideo: React.FC<EmbeddedGiveawayVideoProps> = ({
  id,
  className,
  containerClassName,
  title,
  url,
  thumbnail,
  onPlayClick,
  iframeStyle,
  autoPlayInView,
}) => {
  const [inView, setInView] = useState(false)
  const [loaded, setLoaded] = useState(!thumbnail)
  const modifiedUrl = formatYouTubeUrl(url)

  const handlePlayClick = () => {
    onPlayClick?.()
    setLoaded(true)
  }

  const showThumbnail = thumbnail && !loaded

  const videoContent = (
    <span className={classNames('block aspect-w-16 aspect-h-9 bg-black rounded-2xl', className)}>
      <InView triggerOnce onChange={(inView) => autoPlayInView && setInView(inView)}>
        <iframe
          width="100%"
          height="100%"
          src={mergeQueryParameters(modifiedUrl, { rel: '0', autoplay: inView ? '1' : '0', mute: inView ? '1' : '0' })}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={iframeStyle}
        />
      </InView>
    </span>
  )

  if (showThumbnail) {
    return (
      <div
        id={id}
        style={{ aspectRatio: 16 / 9 }}
        className={classNames('relative w-full overflow-hidden rounded-[10px]', containerClassName)}
      >
        <div
          className="cursor-pointer"
          onClick={handlePlayClick}
          onKeyDown={(e) => e.key === 'Enter' && handlePlayClick()}
          role="button"
          tabIndex={0}
          aria-label={title ?? 'Play video'}
        >
          <Image
            alt="Video player thumbnail"
            src={thumbnail}
            quality={100}
            fill
            className="object-cover object-center"
          />
          <div className="absolute left-1/2 top-1/2 flex h-fit w-fit -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white p-4">
            <PlayIcon size={32} color="core-gray-950" />
          </div>
        </div>
      </div>
    )
  }

  if (loaded && thumbnail) {
    return (
      <div
        id={id}
        style={{ aspectRatio: 16 / 9 }}
        className={classNames('relative w-full overflow-hidden rounded-[10px]', containerClassName)}
      >
        {videoContent}
      </div>
    )
  }

  return videoContent
}
